/**
 * ============================================================================
 * SEARCHSPRING DRAWER (CSS-Only Approach)
 * ============================================================================
 * 
 * Uses CSS to position SearchSpring sidebar as a drawer overlay.
 * No HTML manipulation - sidebar stays in original DOM location.
 * This preserves ALL SearchSpring event listeners and functionality.
 * ============================================================================
 */

(function() {
  'use strict';

  function initDrawer() {
    const drawer = document.getElementById('SearchSpringFilterDrawer');
    if (!drawer) return;
    // Guard: initDrawer runs at ready, again on a 2s retry, and on theme
    // editor section re-renders — bind each drawer element exactly once.
    if (drawer.dataset.ssDrawerBound === '1') return;
    drawer.dataset.ssDrawerBound = '1';

    let lastOpener = null;

    function setExpanded(value) {
      document.querySelectorAll('.js-drawer-open-searchspring-filters').forEach(btn => {
        btn.setAttribute('aria-expanded', value ? 'true' : 'false');
      });
    }

    // Simple function to open drawer (just add class)
    function openDrawer(e) {
      if (e) e.preventDefault();
      lastOpener = e && e.currentTarget && e.currentTarget.focus ? e.currentTarget : document.activeElement;
      document.body.classList.add('searchspring-drawer-open');
      document.documentElement.classList.add('searchspring-drawer-open');
      setExpanded(true);
      const closeBtn = drawer.querySelector('.js-drawer-close-searchspring');
      if (closeBtn) closeBtn.focus();
    }

    // Simple function to close drawer (just remove class)
    function closeDrawer(e) {
      if (e) e.preventDefault();
      document.body.classList.remove('searchspring-drawer-open');
      document.documentElement.classList.remove('searchspring-drawer-open');
      setExpanded(false);
      if (lastOpener && lastOpener.focus) lastOpener.focus();
    }

    // Handle open button clicks
    const openButtons = document.querySelectorAll('.js-drawer-open-searchspring-filters');
    openButtons.forEach(openButton => {
      openButton.addEventListener('click', openDrawer);
    });

    // Handle close button clicks
    const closeButton = drawer.querySelector('.js-drawer-close-searchspring');
    if (closeButton) {
      closeButton.addEventListener('click', closeDrawer);
    }

    // Close drawer when clicking backdrop (outside the sidebar) — bound once
    // per page: section re-renders replace the drawer element (new init) but
    // these document-level listeners resolve everything via live queries.
    if (window.__ssDrawerDocBound) { return; }
    window.__ssDrawerDocBound = true;
    document.addEventListener('click', function(e) {
      if (document.body.classList.contains('searchspring-drawer-open')) {
        const sidebar = document.getElementById('searchspring-sidebar');
        
        if (sidebar) {
          // Check if click is outside the sidebar
          const clickedInsideSidebar = sidebar.contains(e.target);
          
          // Don't close if clicking the open button (would immediately close)
          const isOpenButton = e.target.closest('.js-drawer-open-searchspring-filters');

          // Don't close on clicks inside the drawer's own header overlay —
          // its title/close bar sits outside #searchspring-sidebar, and a
          // mis-tap on the 90vw header would otherwise dismiss the drawer.
          const isDrawerHeader = e.target.closest('#SearchSpringFilterDrawer');

          // Close if clicked outside the sidebar (on backdrop)
          if (!clickedInsideSidebar && !isOpenButton && !isDrawerHeader) {
            closeDrawer(e);
          }
        }
      }
    });

    // Close drawer on ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && document.body.classList.contains('searchspring-drawer-open')) {
        closeDrawer(e);
      }
    });

    // Contain focus inside the open drawer: with the backdrop up and body
    // scroll locked, focus moving past the last control would land on
    // invisible page content behind the backdrop with no way back but ESC.
    // The drawer surface is TWO sibling roots (see main-collection.liquid):
    // the live drawer overlay (re-queried — theme editor section re-renders
    // replace it) holds the heading + close button, and #searchspring-sidebar
    // holds the SearchSpring facet content.
    //
    // Containment is a focusin recapture rather than a keydown wrap: the
    // sidebar carries Searchspring-rendered nodes whose tabbability (zero
    // -size inputs, visibility-hidden collapsed groups) cannot be reliably
    // predicted from a selector list, so instead of trying to mirror the
    // browser's tab order we let focus move and pull it straight back to
    // the correct end whenever it lands outside the drawer surfaces.
    let lastTabWasShift = false;
    function drawerRoots() {
      return {
        overlay: document.getElementById('SearchSpringFilterDrawer'),
        sidebar: document.getElementById('searchspring-sidebar')
      };
    }
    function drawerFocusables() {
      const roots = drawerRoots();
      const sel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return [].concat(
        roots.overlay ? Array.prototype.slice.call(roots.overlay.querySelectorAll(sel)) : [],
        roots.sidebar ? Array.prototype.slice.call(roots.sidebar.querySelectorAll(sel)) : []
      ).filter(function(n) {
        if (n.offsetParent === null) return false;
        if (window.getComputedStyle(n).visibility === 'hidden') return false;
        const r = n.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
      });
    }
    function insideDrawer(n) {
      const roots = drawerRoots();
      return !!n && ((roots.overlay && roots.overlay.contains(n)) || (roots.sidebar && roots.sidebar.contains(n)));
    }
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Tab' && document.body.classList.contains('searchspring-drawer-open')) {
        lastTabWasShift = e.shiftKey;
      }
    });
    document.addEventListener('focusin', function(e) {
      if (!document.body.classList.contains('searchspring-drawer-open')) return;
      if (insideDrawer(e.target)) return;
      const focusables = drawerFocusables();
      if (!focusables.length) return;
      // Focus escaped past an edge: wrap to the opposite end, honouring
      // the direction of the last Tab press.
      (lastTabWasShift ? focusables[focusables.length - 1] : focusables[0]).focus();
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDrawer);
  } else {
    initDrawer();
  }

  // Retry after delay
  setTimeout(initDrawer, 2000);

  // Theme editor section re-renders replace the drawer element
  document.addEventListener('shopify:section:load', initDrawer);

})();
